import { FeatureFlags } from '@fabric-ui/components';
import {
  APP_PATHS,
  PAGE_PATHS,
  Permission,
  PermissionAppName,
  PermissionCategoriesNames,
  PermissionEntitiesNames,
  PermissionForRoute,
  PermissionModelEntityName,
} from '../types';
import { entityProjectionNames } from './entityProjectionNames';

export const appToPermissionCategory: Partial<Record<keyof typeof APP_PATHS, PermissionCategoriesNames>> = {
  [APP_PATHS.DETECTIONS]: PermissionAppName.DETECTIONS,
  [APP_PATHS.INCIDENTS]: PermissionAppName.INCIDENTS,
  [APP_PATHS.VULNERABILITIES]: PermissionAppName.VULNERABILITIES,
  [APP_PATHS.ASSETS]: PermissionAppName.ASSETS,
  [APP_PATHS.RISK360]: PermissionAppName.RISK360,
  [APP_PATHS.THREATS]: PermissionAppName.THREATS,
  [APP_PATHS.IDENTITIES]: PermissionAppName.IDENTITIES,
};

export const getPermissionNameFromValue = ({
  entityName,
  categoryName,
  permission,
}: {
  entityName: PermissionEntitiesNames;
  categoryName?: PermissionCategoriesNames;
  permission: Permission;
}) => (categoryName ? `${categoryName}:${entityName}:${permission}` : `${entityName}:${permission}`);

const appTypeToDefaultEntity = {
  [PermissionAppName.VULNERABILITIES]: PermissionModelEntityName.TICKET,
  [PermissionAppName.INCIDENTS]: PermissionModelEntityName.INCIDENT,
  [PermissionAppName.DETECTIONS]: PermissionModelEntityName.INCIDENT,
  [PermissionAppName.ASSETS]: PermissionModelEntityName.ASSET,
  [PermissionAppName.RISK360]: PermissionModelEntityName.FACTOR,
  [PermissionAppName.THREATS]: PermissionModelEntityName.INCIDENT,
  [PermissionAppName.IDENTITIES]: PermissionModelEntityName.USER,
};

export const appToRuleSetProjection = {
  [PermissionAppName.VULNERABILITIES]: PermissionModelEntityName.TICKET,
  [PermissionAppName.DETECTIONS]: PermissionModelEntityName.INCIDENT,
  [PermissionAppName.INCIDENTS]: PermissionModelEntityName.INCIDENT,
  [PermissionAppName.ASSETS]: PermissionModelEntityName.VIOLATION_TICKET,
};

export const permissionMappingPerRoute = (app?: PermissionAppName): Partial<Record<keyof typeof PAGE_PATHS, PermissionForRoute>> => ({
  [PAGE_PATHS.RULES]: {
    category: PermissionAppName.DETECTIONS,
    permissionEntity: PermissionEntitiesNames.RULE,
  },
  [PAGE_PATHS.CONTENT_PERMISSIONS]: {
    permissionEntity: PermissionEntitiesNames.USER,
  },
  [PAGE_PATHS.MODEL_MANAGEMENT]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.DATA_PURGE]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.ASSET_AGING]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.MEASUREMENTS]: {
    permissionEntity: PermissionEntitiesNames.MEASUREMENT,
  },
  [PAGE_PATHS.DATA_UNIFICATION_FIELDS]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.DATA_UNIFICATION_ENTITIES]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.DATA_UNIFICATION]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.EDIT_MY_ACCOUNT_MANAGEMENT]: {
    permissionEntity: PermissionEntitiesNames.ACCOUNT,
  },
  [PAGE_PATHS.USER_MANAGEMENT]: {
    permissionEntity: PermissionEntitiesNames.USER,
  },
  [PAGE_PATHS.CONNECTED_PRODUCTS]: {
    permissionEntity: PermissionEntitiesNames.ACCOUNT,
  },
  [PAGE_PATHS.ACCOUNT_USAGE]: {
    permissionEntity: PermissionEntitiesNames.ACCOUNT,
  },
  [PAGE_PATHS.EDIT_MY_USER]: {
    permissionEntity: PermissionEntitiesNames.USER,
  },
  [PAGE_PATHS.GLOBAL_SCRIPTS]: {
    category: PermissionAppName.DETECTIONS,
    permissionEntity: PermissionEntitiesNames.GLOBAL_SCRIPT,
  },
  [PAGE_PATHS.TARGETS]: {
    permissionEntity: PermissionEntitiesNames.INTEGRATION,
  },
  [PAGE_PATHS.ALERTS]: {
    category: PermissionModelEntityName.ALERT,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.TEST_QUERY]: {
    category: PermissionModelEntityName.ALERT,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.RULES]: {
    category: PermissionAppName.DETECTIONS,
    permissionEntity: PermissionEntitiesNames.RULE,
  },
  [PAGE_PATHS.SOURCES]: {
    permissionEntity: PermissionEntitiesNames.SOURCE,
  },
  [PAGE_PATHS.SOURCES_MAPPING]: {
    permissionEntity: PermissionEntitiesNames.SOURCE_MAPPING,
  },
  [PAGE_PATHS.SCANNERS_ACTIVATION]: {
    permissionEntity: PermissionEntitiesNames.SOURCE,
  },
  [PAGE_PATHS.INCIDENTS]: {
    category: PermissionModelEntityName.INCIDENT,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.TICKETS]: {
    category: PermissionModelEntityName.TICKET,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.EXCEPTIONS]: {
    category: PermissionAppName.VULNERABILITIES,
    permissionEntity: PermissionEntitiesNames.EXCEPTION,
  },
  [PAGE_PATHS.EXCEPTIONS_SETTINGS]: {
    category: PermissionModelEntityName.EXCEPTION,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.FINDINGS]: {
    category: PermissionModelEntityName.FINDING,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.ASSETS]: {
    category: PermissionModelEntityName.ASSET,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.WORKFLOWS]: {
    permissionEntity: PermissionEntitiesNames.WORKFLOW_SCRIPT,
  },
  [PAGE_PATHS.RULE_SET]: {
    category: app ? appToRuleSetProjection[app] : PermissionModelEntityName.INCIDENT,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.ALERT_SCORE]: {
    category: PermissionModelEntityName.ALERT,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.INCIDENT_RULES]: {
    category: PermissionModelEntityName.INCIDENT,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.SEVERITY_SLA]: {
    category: PermissionModelEntityName.TICKET,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.SEVERITY]: {
    category: PermissionModelEntityName.ALERT,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.TICKETS_STATUSES]: {
    category:
      {
        [PermissionAppName.VULNERABILITIES]: PermissionModelEntityName.TICKET,
        [PermissionAppName.INCIDENTS]: PermissionModelEntityName.INCIDENT,
        [PermissionAppName.THREATS]: PermissionModelEntityName.INCIDENT,
      }[app!] || PermissionModelEntityName.VIOLATION_TICKET,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.SCORE]: {
    category: PermissionModelEntityName.TICKET,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.UI_CONFIG]: {
    category: appTypeToDefaultEntity[app!], // TODO: change this function to get the entity type as input, since some of the permissions are based on entities
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.INCIDENT_LIFECYCLE]: {
    category: PermissionModelEntityName.INCIDENT,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.SETTINGS]: {
    category: appTypeToDefaultEntity[app!],
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.LOGS]: {
    permissionEntity: PermissionEntitiesNames.SEARCH,
  },
  [PAGE_PATHS.ENTITIES]: {
    permissionEntity: PermissionEntitiesNames.SEARCH,
  },
  [PAGE_PATHS.LOOKUP_TABLES]: {
    category: PermissionAppName.DETECTIONS,
    permissionEntity: PermissionEntitiesNames.LOOKUP_TABLE,
  },
  [PAGE_PATHS.REMEDIATION]: ({
    [PermissionAppName.VULNERABILITIES]: {
      category: PermissionAppName.VULNERABILITIES,
      permissionEntity: PermissionEntitiesNames.ANALYTICS,
    },
    [PermissionAppName.IDENTITIES]: {
      category: PermissionModelEntityName.IDENTITY_FINDING,
      permissionEntity: PermissionEntitiesNames.OPT,
    },
  }[app!] || { permissionEntity: PermissionEntitiesNames.BACKOFFICE }) as PermissionForRoute,
  [PAGE_PATHS.RISK]: {
    category: PermissionAppName.VULNERABILITIES,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.ASSET_COVERAGE]: {
    category: PermissionModelEntityName.ASSET,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.PIVOT]: {
    category:
      app === PermissionAppName.VULNERABILITIES
        ? PermissionAppName.VULNERABILITIES
        : app === PermissionAppName.DETECTIONS
          ? PermissionAppName.DETECTIONS
          : PermissionAppName.INCIDENTS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.OVERVIEW]: ({
    [PermissionAppName.VULNERABILITIES]: {
      category: PermissionAppName.VULNERABILITIES,
      permissionEntity: PermissionEntitiesNames.ANALYTICS,
    },
    [PermissionAppName.IDENTITIES]: {
      category: PermissionModelEntityName.IDENTITY_FINDING,
      permissionEntity: PermissionEntitiesNames.OPT,
    },
    [PermissionAppName.PLATFORM]: {
      permissionEntity: PermissionEntitiesNames.SOURCE,
    },
    [PermissionAppName.THREATS]: {
      category: PermissionAppName.THREATS,
      permissionEntity: PermissionEntitiesNames.ANALYTICS,
    },
  }[app!] || { permissionEntity: PermissionEntitiesNames.BACKOFFICE }) as PermissionForRoute,
  [PAGE_PATHS.REPORTS]: {
    permissionEntity: PermissionEntitiesNames.REPORT,
  },
  [PAGE_PATHS.CUSTOM_DASHBOARDS]: {
    permissionEntity: PermissionEntitiesNames.REPORT,
  },
  [PAGE_PATHS.ALIASES]: {
    permissionEntity: PermissionEntitiesNames.ALIAS,
    relevantFeatureFlag: FeatureFlags.ShowAliasPage,
  },
  [PAGE_PATHS.RELATIONS]: {
    permissionEntity: PermissionEntitiesNames.ALIAS,
  },
  [PAGE_PATHS.ENTITIES_TO_LOGS]: {
    permissionEntity: PermissionEntitiesNames.ALIAS,
  },
  [PAGE_PATHS.ROLES]: {
    permissionEntity: PermissionEntitiesNames.ROLE,
  },
  [PAGE_PATHS.FACTOR_RULES]: {
    category: PermissionAppName.RISK360,
    permissionEntity: PermissionEntitiesNames.EXE_RULE,
  },
  [PAGE_PATHS.POLICIES]: {
    category: PermissionAppName.ASSETS,
    permissionEntity: PermissionEntitiesNames.EXE_RULE,
  },
  [PAGE_PATHS.POLICY_COMPLIANCE]: {
    category: PermissionAppName.ASSETS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.ASSET_INVENTORY]: {
    category: PermissionAppName.ASSETS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.TOOL_COVERAGE_AND_GAP]: {
    category: PermissionAppName.ASSETS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.BACKOFFICE_ACTIONS]: {
    permissionEntity: PermissionEntitiesNames.BACKOFFICE,
  },
  [PAGE_PATHS.GLOBAL_FACTOR_RULES]: {
    category: PermissionAppName.RISK360,
    permissionEntity: PermissionEntitiesNames.BUILTIN_EXE_RULE,
  },
  [PAGE_PATHS.ICONS]: {
    permissionEntity: PermissionEntitiesNames.BACKOFFICE,
  },
  [PAGE_PATHS.ALL_ACCOUNTS]: {
    permissionEntity: PermissionEntitiesNames.ACCOUNT,
    permission: Permission.CREATE,
  },
  [PAGE_PATHS.ALL_USERS]: {
    permissionEntity: PermissionEntitiesNames.BACKOFFICE,
  },
  [PAGE_PATHS.QUERY]: {
    permissionEntity: PermissionEntitiesNames.QUERY_RUNNER,
  },
  [PAGE_PATHS.POLICY_VIOLATIONS]: {
    category: PermissionModelEntityName.POLICYVIOLATION,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.AUTHENTICATION_SOURCES]: {
    permissionEntity: PermissionEntitiesNames.AUTHENTICATION,
  },
  [PAGE_PATHS.RISK360_BACKOFFICE_ACTIONS]: {
    category: PermissionAppName.RISK360,
    permissionEntity: PermissionEntitiesNames.BUILTIN_EXE_RULE,
  },
  [PAGE_PATHS.FORMATTING_RULES]: {
    permissionEntity: PermissionEntitiesNames.REPORT,
  },
  [PAGE_PATHS.EASM_SETTINGS]: {
    permissionEntity: PermissionEntitiesNames.EASM_CONFIGURATION,
    permission: Permission.READ,
  },
  [PAGE_PATHS.VIOLATION_TICKETS]: {
    category: PermissionModelEntityName.VIOLATION_TICKET,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.TICKET_LIFECYCLE]: {
    category: {
      [PermissionAppName.VULNERABILITIES]: PermissionModelEntityName.TICKET,
      [PermissionAppName.ASSETS]: PermissionModelEntityName.VIOLATION_TICKET,
    }[app!],
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.AUTOMATIONS]: {
    category: {
      [PermissionAppName.VULNERABILITIES]: PermissionModelEntityName.TICKET,
      [PermissionAppName.ASSETS]: PermissionModelEntityName.VIOLATION_TICKET,
    }[app!],
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.EXCEPTIONS_STATUSES]: {
    category: PermissionModelEntityName.EXCEPTION,
    permissionEntity: PermissionEntitiesNames.SETTINGS,
  },
  [PAGE_PATHS.TENANTS]: {
    category: PermissionModelEntityName.TENANT,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.IDENTITY_FINDINGS]: {
    category: PermissionModelEntityName.IDENTITY_FINDING,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.USERS]: {
    category: PermissionModelEntityName.USER,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.IDENTITY]: {
    category: PermissionModelEntityName.IDENTITY,
    permissionEntity: PermissionEntitiesNames.OPT,
  },
  [PAGE_PATHS.ITDR_CONFIGURATION]: {
    category: PermissionAppName.IDENTITIES,
    permissionEntity: PermissionEntitiesNames.ITDR_CONFIGURATION,
  },
  [PAGE_PATHS.THREAT_DASHBOARD]: {
    category: PermissionAppName.THREATS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.PIPELINE_MONITORING_STATUS]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.PIPELINE_MONITORING_TRENDS]: {
    permissionEntity: PermissionEntitiesNames.MODEL,
  },
  [PAGE_PATHS.AGENTS]: {
    category: PermissionAppName.THREATS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.RISK_SCORE]: {
    permissionEntity: PermissionEntitiesNames.ASSET_RISK_SCORE,
    relevantFeatureFlag: FeatureFlags.AssetRiskScore,
  },
  [PAGE_PATHS.AGENTIC_SOC]: {
    category: PermissionAppName.THREATS,
    permissionEntity: PermissionEntitiesNames.ANALYTICS,
  },
  [PAGE_PATHS.SOC_AUTOMATIONS]: {
    permissionEntity: PermissionEntitiesNames.VIEWS,
  },
  [PAGE_PATHS.AUTOMATION_RULES]: {
    permissionEntity: PermissionEntitiesNames.AUTOMATION_RULE,
  },
  [PAGE_PATHS.PLAYBOOKS]: {
    permissionEntity: PermissionEntitiesNames.PLAYBOOK,
  },
  [PAGE_PATHS.RULE_ENGINE]: {
    category: PermissionAppName.IDENTITIES,
    permissionEntity: PermissionEntitiesNames.EXE_RULE,
  },
});

export const fullPermissionsToResource = (value = true): { [key in Permission]: boolean } =>
  Object.keys(Permission).reduce((acc, key) => ({ ...acc, [key]: value }), {} as { [key in Permission]: boolean });

export const projectionNameToCategoryName = {
  [entityProjectionNames.Ticket]: PermissionModelEntityName.TICKET,
  [entityProjectionNames.Finding]: PermissionModelEntityName.FINDING,
  [entityProjectionNames.Asset]: PermissionModelEntityName.ASSET,
  [entityProjectionNames.Incident]: PermissionModelEntityName.INCIDENT,
  [entityProjectionNames.Alert]: PermissionModelEntityName.ALERT,
  [entityProjectionNames.PolicyViolation]: PermissionModelEntityName.POLICYVIOLATION,
  [entityProjectionNames.ViolationTicket]: PermissionModelEntityName.VIOLATION_TICKET,
  [entityProjectionNames.Exception]: PermissionModelEntityName.EXCEPTION,
  [entityProjectionNames.Tenant]: PermissionModelEntityName.TENANT,
  [entityProjectionNames.User]: PermissionModelEntityName.USER,
  [entityProjectionNames.Identity]: PermissionModelEntityName.IDENTITY,
  [entityProjectionNames.IdentityFinding]: PermissionModelEntityName.IDENTITY_FINDING,
  [entityProjectionNames.GlobalVulnerability]: 'GLOBALVULNERABILITY',
};
